import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthProvider";
import { sendOtp, verifyOtp } from "../../services/auth";
import { type TVerifyOtpRequest } from "../../services/auth/types";
import { sendUtm } from "../../services/utm";
import { getWebinar } from "../../services/webinar";
import { convertToEnglishNumber, getOtpSendErrorMessage, getUtmData } from "../../utils";
import { Spinner } from "../shared";
import { loginSchema, type TLoginFormData } from "./validations";

interface LoginModalProps {
  onClose: () => void;
}

export const LoginModal = ({ onClose }: LoginModalProps) => {
  const navigate = useNavigate();
  const [showLastStep, setShowLastStep] = useState(false);
  const [timer, setTimer] = useState(120);
  const [isResendVisible, setIsResendVisible] = useState(false);
  const [otp, setOtp] = useState("");
  const { login } = useAuth();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TLoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const { mutate: getWebinarEnroll } = useMutation({
    mutationFn: getWebinar,
  });

  const { mutate: sendUserUtm } = useMutation({
    mutationFn: sendUtm,
  });

  const { mutate, isPending: isSendOtpPending } = useMutation({
    mutationFn: sendOtp,
  });

  const { mutate: checkOtp, isPending: isVerifyOtpPending } = useMutation<
    TVerifyOtpRequest["response"],
    Error,
    TVerifyOtpRequest["payload"]
  >({
    mutationFn: verifyOtp,
  });

  useEffect(() => {
    if (!showLastStep || timer <= 0) {
      return;
    }

    const interval = setInterval(() => {
      setTimer((t) => {
        if (t - 1 <= 0) setIsResendVisible(true);
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showLastStep, timer]);

  const onSubmit = (data: TLoginFormData) => {
    const phoneNumber = convertToEnglishNumber(data.phoneNumber);
    if (!showLastStep) {
      mutate(
        {
          phoneNumber,
          fullName: data.fullName,
        },
        {
          onSuccess: (data) => {
            if (data.data.message) {
              toast.success("کد تایید ارسال شد");
            }
            setShowLastStep(true);
            setTimer(120);
            setIsResendVisible(false);
            setShowLastStep(true);
            setOtp(data.data.data.otp);
            checkOtp(
              {
                phoneNumber,
                otp: data.data.data.otp,
              },
              {
                onSuccess: (data) => {
                  const { token, tokenExpires, user } = data;
                  login(user, token, tokenExpires);
                  toast.success("ورود با موفقیت انجام شد 🎉");
                  const hasSentUtm = localStorage.getItem("utm_sent");
                  if (!hasSentUtm) {
                    const utm = getUtmData();
                    sendUserUtm(utm, {
                      onSuccess: () => {
                        localStorage.setItem("utm_sent", "true");
                      },
                    });
                  }
                  getWebinarEnroll(
                    {
                      webinarId: 6,
                    },
                    {
                      onSuccess: () => {
                        onClose();
                        reset();
                        setTimer(0);
                        navigate("/mini-course/6");
                      },
                      onError: () => {
                        toast.error("کاربر در وبینار ثبت نشد");
                      },
                    }
                  );
                },
                onError: () => {
                  toast.error("کد وارد شده اشتباه یا نامعتبر است ❌");
                },
              }
            );
          },
          onError: (error) => {
            toast.error(getOtpSendErrorMessage(error));
          },
        }
      );
    } else {
      checkOtp(
        {
          phoneNumber,
          otp: otp,
        },
        {
          onSuccess: (data) => {
            const { token, tokenExpires, user } = data;
            login(user, token, tokenExpires);
            toast.success("ورود با موفقیت انجام شد 🎉");
            const hasSentUtm = localStorage.getItem("utm_sent");
            if (!hasSentUtm) {
              const utm = getUtmData();
              sendUserUtm(utm, {
                onSuccess: () => {
                  localStorage.setItem("utm_sent", "true");
                },
              });
            }
            getWebinarEnroll(
              {
                webinarId: 6,
              },
              {
                onSuccess: () => {
                  onClose();
                  reset();
                  setTimer(0);
                  navigate("/mini-course/6");
                },
                onError: () => {
                  toast.error("کاربر در وبینار ثبت نشد");
                },
              }
            );
          },
          onError: () => {
            toast.error("کد وارد شده اشتباه یا نامعتبر است ❌");
          },
        }
      );
    }
  };

  const handleResend = () => {
    toast.info("کد جدید ارسال شد");
    setTimer(120);
    setIsResendVisible(false);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => onClose()}
      >
        <motion.div
          className="w-[327px] md:w-lg min-h-[445px] bg-white/70 rounded-2xl shadow-lg p-6 flex flex-col justify-center"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", duration: 0.4 }}
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src="/images/user.png"
            alt="User"
            width={55}
            height={55}
            className="mb-6 mx-auto"
          />
          <h2 className="text-2xl font-kalameh font-bold text-center mb-6 hover:cursor-pointer">
            ثبت نام رایگان و شروع
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <input
                disabled={showLastStep}
                {...register("phoneNumber", {
                  onChange: (e) => {
                    e.target.value = convertToEnglishNumber(e.target.value);
                  }
                })}
                placeholder="شماره تلفن همراه"
                className="w-full h-[55px] border border-gray-300 font-kalameh text-[15px] rounded-lg px-4 text-gray-700 bg-white outline-none focus:border-black"
              />
              {errors.phoneNumber && (
                <p className="text-red-500 text-sm mt-1 font-kalameh">
                  {errors.phoneNumber.message}
                </p>
              )}
            </div>
            <div>
              <input
                disabled={showLastStep}
                {...register("fullName")}
                placeholder="نام و نام خانوادگی"
                className="w-full h-[55px] border border-gray-300 font-kalameh text-[15px] rounded-lg px-4 text-gray-700 bg-white outline-none focus:border-black"
              />
              {errors.fullName && (
                <p className="text-red-500 text-sm mt-1 font-kalameh">
                  {errors.fullName.message}
                </p>
              )}
            </div>

            {showLastStep && (
              <div className="space-y-6">
                <p className="font-kalameh text-gray-700">
                  کد تایید ارسال شده را وارد کنید:
                </p>

                <div className="flex items-center justify-between gap-3">
                  {!isResendVisible ? (
                    <div className="w-[100px] h-[55px] flex items-center justify-center rounded-lg bg-gray-200 font-kalameh text-gray-700">
                      {formatTime(timer)}
                    </div>
                  ) : (
                    <button
                      onClick={handleResend}
                      className="w-[100px] h-[55px] bg-black text-white rounded-lg font-kalameh hover:bg-gray-800 transition"
                    >
                      ارسال مجدد
                    </button>
                  )}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => {
                      const converted = convertToEnglishNumber(e.target.value);
                      setOtp(converted.replace(/\D/g, ""));
                    }}
                    placeholder="کد تایید"
                    className="flex-1 h-[55px] border border-gray-300 rounded-lg px-4 font-kalameh text-[15px] text-gray-700 bg-white outline-none focus:border-black"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSendOtpPending || isVerifyOtpPending}
              className={`w-full h-[55px] font-kalameh text-[16px] rounded-lg mt-3 flex items-center justify-center gap-2 transition
    ${isSendOtpPending || isVerifyOtpPending
                  ? "bg-gray-700 cursor-not-allowed"
                  : "bg-black hover:bg-gray-800 text-white"
                }
  `}
            >
              {(isSendOtpPending || isVerifyOtpPending) && <Spinner />}
              <span>
                {isSendOtpPending && !showLastStep
                  ? "در حال ارسال..."
                  : showLastStep
                    ? "وارد شو!"
                    : "ارسال کد تایید"}
              </span>
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
